// ─────────────────────────────────────────────────────────────────────────────
// stores/printJobStore.ts – PrintJob Pinia Store
//
// Reichweite der Auftragsliste (REQ-OPS-033, T-M9.3): KEINE feste Obergrenze
// mehr, sondern Nachladen in Seiten zu `JOBS_PAGE_SIZE`. Live ist ausschliesslich
// die erste Seite; nachgeladene Seiten stehen still. Die Regeln der Seitengrenze
// (Zusammenfuehren, Doppel- und Luecken-Vermeidung, Ende der Liste) liegen als
// reine Funktionen in `utils/jobPagination.ts` und sind dort ohne Mount geprueft.
//
// KENNZAHLEN SIND NICHT DIE LISTE (REQ-OPS-034, T-M10.3): dieser Store ist
// GETEILT — die Kennzahl-Kacheln der Uebersicht und das Abzeichen der Navigation
// lesen ihn mit, laden aber NIE nach. Aus der Liste abgeleitete Kennzahlen
// zaehlten deshalb nur das 50er-Live-Fenster (Befund OP-72). Sie kommen jetzt
// aus einer eigenen, serverseitigen Zaehlung (`countJobStats`), die im festen
// Takt erneuert wird. Der frueher hier gefuehrte `stats`-Getter ist ERSATZLOS
// entfallen; an seiner Stelle steht `countedStats`, das je Zahl mitfuehrt, ob
// sie gezaehlt oder nur eine Untergrenze ist. Der Wegfall ist Absicht: ein
// Aufrufer, der die alte Zahl weiterverwendet, bricht in der Typpruefung, statt
// still weiter zu wenig zu zeigen.
// ─────────────────────────────────────────────────────────────────────────────

import { defineStore } from 'pinia';
import { ref, computed, shallowRef } from 'vue';
import type { Unsubscribe } from 'firebase/firestore';

import type { PrintJob }       from '@/firebase/models';
import { PrintJobStatus }      from '@/firebase/models';
import {
  subscribeToJobs,
  fetchJobsPage,
  createPrintJob,
  countJobStats,
  computeJobStats,
  type CreatePrintJobParams,
  type JobStats,
  type JobsPage,
  type JobsPageCursor,
} from '@/services/printJobService';
import {
  JOBS_PAGE_SIZE,
  mergeJobPages,
  retainedAfterLiveUpdate,
  canLoadMore,
} from '@/utils/jobPagination';
import {
  createRefreshTimer,
  isHeldJob,
  resolveJobStats,
  shouldAcceptCounts,
  type CountedJobStats,
  type JobStatCounts,
  type RefreshTimer,
} from '@/utils/jobCounts';

export const usePrintJobStore = defineStore('printJobs', () => {
  // ── State ──────────────────────────────────────────────────────────────────
  /** Erste Seite — laufend aktualisiert (onSnapshot). */
  const livePage    = ref<PrintJob[]>([]);
  /** Nachgeladene Seiten — statisch, in Ladereihenfolge. */
  const staticPages = ref<PrintJob[][]>([]);
  /** Unter die Live-Seite abgesunkene Zeilen (sonst entstuende dort eine Luecke). */
  const retained    = ref<PrintJob[]>([]);

  const isLoading     = ref(false);
  const isLoadingMore = ref(false);
  const error         = ref<string | null>(null);
  /** Fehler des Nachladens — getrennt, damit er die geladene Liste nicht ersetzt. */
  const loadMoreError = ref<string | null>(null);
  const statusFilter  = ref<PrintJobStatus | 'all'>('all');

  /** Rohe Dokumentzahl der Live-Seite bzw. der zuletzt nachgeladenen Seite. */
  const livePageSize   = ref(0);
  const lastPageSize   = ref<number | null>(null);

  /**
   * Serverseitig gezaehlte Kennzahlen (REQ-OPS-034) — `null`, solange keine
   * Zaehlung vorliegt. Dann und nur dann greift die Untergrenze aus dem Fenster.
   */
  const counts      = ref<JobStatCounts | null>(null);
  const countsError = ref<string | null>(null);

  /** Haelt der Store gerade einen Listener (REQ-OPS-053 (a) (ii), OP-151)? */
  const isSubscribed = ref(false);

  /** Active Firestore unsubscribe function. */
  let _unsubscribe: Unsubscribe | null = null;
  /** Mandant der laufenden Abfrage — das Nachladen braucht denselben Filter. */
  let _companyId: string | null = null;
  /** Cursor der Live-Seite; nur gueltig, solange nichts nachgeladen wurde. */
  const _liveCursor   = shallowRef<JobsPageCursor | null>(null);
  /** Cursor der zuletzt nachgeladenen Seite — eingefroren, sobald gesetzt. */
  const _staticCursor = shallowRef<JobsPageCursor | null>(null);
  /** Takt der Zaehlung; `null`, solange nichts abonniert ist (REQ-OPS-034). */
  let _countTimer: RefreshTimer | null = null;

  // ── Computed ───────────────────────────────────────────────────────────────

  /**
   * Die angezeigte Liste: Live-Seite, nachgeladene Seiten und abgesunkene
   * Zeilen in EINER, nach der Abfragereihenfolge sortierten Liste ohne
   * doppelte Kennungen (REQ-OPS-033).
   */
  const jobs = computed<PrintJob[]>(
    () => mergeJobPages(livePage.value, staticPages.value, retained.value),
  );

  /**
   * Was die GELADENE Liste hergibt — ausdruecklich nur eine Untergrenze, weil
   * live nur die erste Seite ist (REQ-OPS-033). Nicht nach aussen gegeben:
   * diese Zahl darf nirgends als exakte Kennzahl erscheinen (REQ-OPS-034).
   */
  const windowStats = computed<JobStats>(() => computeJobStats(jobs.value));

  /**
   * Die Kennzahlen der Uebersicht und das Abzeichen der Navigation — je mit der
   * Auskunft, ob die Zahl serverseitig gezaehlt (`exact: true`) oder bloss aus
   * dem Live-Fenster abgeleitet ist (`exact: false`, Untergrenze).
   *
   * `pending` ist seit T-M15.6 die WARTENDE Menge ohne die zurueckgehaltenen
   * Auftraege; diese stehen als eigene, benannte Menge daneben
   * (`pendingHeld`, REQ-LC-015).
   */
  const countedStats = computed<CountedJobStats>(
    () => resolveJobStats(counts.value, windowStats.value),
  );

  /** Liegt fuer alle Kacheln eine serverseitige Zaehlung vor? */
  const countsExact = computed(() => counts.value !== null);

  const filteredJobs = computed<PrintJob[]>(() => {
    if (statusFilter.value === 'all') return jobs.value;
    return jobs.value.filter((j) => j.status === statusFilter.value);
  });

  /**
   * Die WARTENDEN Auftraege des Live-Fensters — `pending` OHNE die
   * zurueckgehaltenen (REQ-LC-015).
   *
   * ⚠ Der frueher hier gefuehrte Getter `pendingJobs` ist ERSATZLOS entfallen,
   * aus demselben Grund, aus dem T-M10.3 den Getter `stats` entfernt hat: sein
   * einziger Verwender ist die Rueckfallzahl des Warteschlangen-Abzeichens
   * (Zaehlstelle 5, REQ-LC-015 Praez. 1 (a)), und die folgt der Zahl, die sie
   * traegt — dort gilt die engere Form. Haette der alte Name mit unveraenderter
   * Bedeutung danebengestanden, zaehlte ein kuenftiger Aufrufer die gehaltenen
   * still wieder mit; so bricht er in der Typpruefung.
   *
   * Die Trennung laeuft ueber `isHeldJob` (`=== true`) und nicht ueber ein
   * `!== false`: ein Bestandsdokument OHNE das Feld bleibt damit WARTEND.
   */
  const waitingJobs   = computed(
    () => jobs.value.filter((j) => j.status === PrintJobStatus.PENDING && !isHeldJob(j)),
  );
  const printingJobs  = computed(() => jobs.value.filter((j) => j.status === PrintJobStatus.PRINTING));

  /** Sind weitere Auftraege nachladbar? */
  const hasMore = computed(
    () => canLoadMore(livePageSize.value, lastPageSize.value) && !!nextCursor(),
  );

  /** Zahl der geladenen Seiten (1 = nur die Live-Seite). */
  const loadedPages = computed(() => 1 + staticPages.value.length);

  /**
   * Steht ein Teil der Liste still? Ab der ersten nachgeladenen Seite gilt die
   * Live-Aktualisierung nur noch fuer die erste Seite — das ist in der
   * Oberflaeche zu sagen, statt es zu verschweigen.
   */
  const hasStaticPages = computed(() => staticPages.value.length > 0);

  // ── Actions ────────────────────────────────────────────────────────────────

  /** Cursor, an dem die naechste Seite ansetzt. */
  function nextCursor(): JobsPageCursor | null {
    return staticPages.value.length ? _staticCursor.value : _liveCursor.value;
  }

  /** Seitenzustand zuruecksetzen (Mandantenwechsel, Filterwechsel, Abmeldung). */
  function resetPages(): void {
    livePage.value    = [];
    staticPages.value = [];
    retained.value    = [];
    livePageSize.value  = 0;
    lastPageSize.value  = null;
    _liveCursor.value   = null;
    _staticCursor.value = null;
    loadMoreError.value = null;
    isLoadingMore.value = false;
    // Beim Mandantenwechsel darf keine Zahl des vorigen Mandanten nachhallen.
    counts.value      = null;
    countsError.value = null;
  }

  /**
   * Die Kennzahlen serverseitig neu zaehlen (REQ-OPS-034).
   *
   * Das Ergebnis wird verworfen, wenn inzwischen ein anderer Mandant gewaehlt
   * wurde: `count()` ist asynchron, und eine spaet eintreffende Antwort traegt
   * sonst die Zahl des vorigen Mandanten in die neue Ansicht.
   */
  async function refreshCounts(): Promise<void> {
    const requestedFor = _companyId;
    if (!requestedFor) return;

    try {
      const result = await countJobStats(requestedFor);
      if (!shouldAcceptCounts(requestedFor, _companyId)) return;
      counts.value      = result;
      countsError.value = null;
    } catch (err: unknown) {
      if (!shouldAcceptCounts(requestedFor, _companyId)) return;
      // Die Zahlen bleiben stehen, was zuletzt gezaehlt wurde; liegt noch nichts
      // vor, greift die als Untergrenze gekennzeichnete Fensterzahl.
      countsError.value = (err as { message?: string }).message ?? 'Unbekannter Fehler';
    }
  }

  /**
   * Start the real-time Firestore listener for the given company.
   * Call this once after the user logs in. Call unsubscribe() on logout.
   */
  function subscribe(companyId: string): void {
    // Unsubscribe from any previous listener before creating a new one.
    // Das bricht zugleich eine laufende Rueckzugsstaffel ab: ohne den Abbruch
    // abonnierte der Store nach einem Mandantenwechsel den VORIGEN Mandanten
    // nach (REQ-OPS-053 (d)).
    if (_unsubscribe) _unsubscribe();

    resetPages();
    _companyId         = companyId;
    isLoading.value    = true;
    error.value        = null;
    isSubscribed.value = true;

    // Eigener Takt fuer die Kennzahlen (REQ-OPS-034). `start()` raeumt einen
    // vorigen Takt selbst ab — beim Mandantenwechsel bleibt kein Timer stehen.
    if (!_countTimer) _countTimer = createRefreshTimer(() => { void refreshCounts(); });
    _countTimer.start();

    _unsubscribe = subscribeToJobs({
      companyId,
      statusFilter: statusFilter.value,
      pageSize:     JOBS_PAGE_SIZE,
      onData: (page: JobsPage) => {
        // Vor dem Ueberschreiben festhalten, was unter die Live-Seite absinkt —
        // sonst risse an der Seitengrenze eine Luecke auf (T-M9.3).
        retained.value = retainedAfterLiveUpdate(
          jobs.value, page.jobs, staticPages.value,
        );
        livePage.value     = page.jobs;
        livePageSize.value = page.size;
        // Solange nichts nachgeladen wurde, wandert der Cursor mit der
        // Live-Seite mit; danach ist er eingefroren.
        if (staticPages.value.length === 0) _liveCursor.value = page.cursor;
        isLoading.value = false;
        // Zusaetzlich zum Takt: veraendert sich die Live-Seite, ist die Zahl
        // veraltet. `poke()` laesst den Lauf aus, wenn er zu dicht auf dem
        // letzten folgt — der Takt holt ihn dann ein.
        _countTimer?.poke();
      },
      // Der Zustand bleibt WAHR (OP-151): Ladezustand faellt, das
      // „abonniert"-Merkmal wird zurueckgenommen, die Meldung ist gesetzt.
      onError: (err) => {
        error.value        = `Fehler beim Laden der Druckaufträge: ${err.message}`;
        isLoading.value    = false;
        isSubscribed.value = false;
      },
      // Der Rueckweg (REQ-OPS-053 (a), Gegenrichtung): die Staffel hat das Abo
      // zurueckgeholt — also faellt auch die Meldung, sonst behauptete der
      // Rahmen-Hinweis (T-M21.5) „nicht mehr live" ueber Auftraege, die gerade
      // wieder eintreffen.
      onRecovered: () => {
        error.value        = null;
        isSubscribed.value = true;
      },
      // Abmeldung: still, kein Fehlzustand (REQ-OPS-053 (c)).
      onStill: () => {
        isLoading.value    = false;
        isSubscribed.value = false;
      },
    });
  }

  /**
   * Naechste Seite nachladen (REQ-OPS-033).
   *
   * Die geladene Seite ist statisch: sie haengt an einem Cursor und wird nicht
   * mehr aktualisiert. Fehler werden gemeldet, nicht verschluckt — sonst sieht
   * die Ansicht wie das Ende der Liste aus.
   */
  async function loadMore(): Promise<void> {
    const cursor = nextCursor();
    if (!_companyId || !cursor || isLoadingMore.value || !hasMore.value) return;

    isLoadingMore.value = true;
    loadMoreError.value = null;
    try {
      const page = await fetchJobsPage({
        companyId:     _companyId,
        statusFilter:  statusFilter.value,
        startAfterDoc: cursor,
        pageSize:      JOBS_PAGE_SIZE,
      });
      staticPages.value  = [...staticPages.value, page.jobs];
      lastPageSize.value = page.size;
      if (page.cursor) _staticCursor.value = page.cursor;
    } catch (err: unknown) {
      const message = (err as { message?: string }).message ?? 'Unbekannter Fehler';
      loadMoreError.value = message;
    } finally {
      isLoadingMore.value = false;
    }
  }

  /** Stop the Firestore listener and clear the store. */
  function unsubscribe(): void {
    if (_unsubscribe) {
      _unsubscribe();
      _unsubscribe = null;
    }
    // Ein stehengebliebener Takt ist ein Befund, kein Detail (REQ-OPS-034).
    _countTimer?.stop();
    resetPages();
    _companyId         = null;
    isLoading.value    = false;
    error.value        = null;
    isSubscribed.value = false;
  }

  /** Create a new print job from the dashboard. */
  async function createJob(params: CreatePrintJobParams): Promise<string> {
    error.value = null;
    try {
      const jobId = await createPrintJob(params);
      return jobId;
    } catch (err: unknown) {
      const message = (err as { message?: string }).message ?? 'Unbekannter Fehler';
      error.value = `Druckauftrag konnte nicht erstellt werden: ${message}`;
      throw err;
    }
  }

  /** Change the active status filter and restart the subscription. */
  function setStatusFilter(filter: PrintJobStatus | 'all', companyId: string): void {
    statusFilter.value = filter;
    subscribe(companyId);
  }

  return {
    jobs,
    filteredJobs,
    waitingJobs,
    printingJobs,
    countedStats,
    countsExact,
    countsError,
    isLoading,
    isLoadingMore,
    isSubscribed,
    error,
    loadMoreError,
    statusFilter,
    hasMore,
    loadedPages,
    hasStaticPages,
    subscribe,
    loadMore,
    refreshCounts,
    unsubscribe,
    createJob,
    setStatusFilter,
  };
});
